/**
 * Side effect reports (Treatment Plan spec v1.4, LR-18, EV-50). The provider
 * records what a patient reported, with a severity and the item; the record
 * joins the legal audit trail. MedWatch opens the FDA's page so the prescriber
 * can report there, and the record notes that it was submitted.
 *
 * Used on the patient profile, and from a clinical conversation in Messages,
 * where the patient's own words fill the description.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button, Card, Field, LoadingRows, inputClass } from './ui';

interface SideEffectsData {
  medwatchUrl: string;
  items: Array<{ id: string; name: string }>;
  events: Array<{
    id: string;
    severity: 'mild' | 'moderate' | 'severe' | 'life_threatening';
    description: string;
    source: string;
    itemName: string | null;
    onsetOn: string | null;
    productProblem: boolean;
    medwatchSubmitted: boolean;
    medwatchRef: string | null;
    recordedBy: string | null;
    createdAt: string;
  }>;
}

const SEVERITY: Array<[SideEffectsData['events'][number]['severity'], string]> = [
  ['mild', 'Mild'],
  ['moderate', 'Moderate'],
  ['severe', 'Severe'],
  ['life_threatening', 'Life-threatening'],
];
const SEVERITY_CHIP: Record<string, string> = {
  mild: 'bg-mint text-forest',
  moderate: 'bg-risk-yellow-soft text-risk-yellow',
  severe: 'bg-alert-soft text-alert',
  life_threatening: 'bg-alert text-white',
};

const key = (patientId: string) => ['adverse-events', patientId] as const;

/** The form on its own: used inline in a conversation. */
export function RecordSideEffect({
  patientId,
  source = 'other',
  sourceRef = null,
  prefill = '',
  onDone,
}: {
  patientId: string;
  source?: 'message' | 'rx_problem' | 'visit' | 'check_in' | 'other';
  sourceRef?: string | null;
  prefill?: string;
  onDone?: () => void;
}) {
  const qc = useQueryClient();
  const data = useQuery({ queryKey: key(patientId), queryFn: () => api.get<SideEffectsData>(`/patients/${patientId}/adverse-events`) });
  const [severity, setSeverity] = useState<SideEffectsData['events'][number]['severity'] | ''>('');
  const [itemId, setItemId] = useState('');
  const [description, setDescription] = useState(prefill);
  const [onsetOn, setOnsetOn] = useState('');
  const [productProblem, setProductProblem] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api.post(`/patients/${patientId}/adverse-events`, {
        severity,
        description,
        source,
        sourceRef,
        itemId: itemId || null,
        onsetOn: onsetOn || null,
        productProblem,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(patientId) });
      setSeverity('');
      setDescription('');
      setOnsetOn('');
      setProductProblem(false);
      onDone?.();
    },
  });
  return (
    <div className="grid gap-3" data-testid="record-side-effect">
      <div role="radiogroup" aria-label="Severity" className="flex flex-wrap gap-2">
        {SEVERITY.map(([value, text]) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={severity === value}
            onClick={() => setSeverity(value)}
            className={`rounded-full border px-3.5 py-1.5 text-[13px] font-medium ${severity === value ? 'border-forest bg-[#e6f0ea] text-forest' : 'border-[#d8d3c8] bg-white text-navy hover:bg-[#f6f5f1]'}`}
          >
            {text}
          </button>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Medication">
          <select className={inputClass} value={itemId} onChange={(e) => setItemId(e.target.value)}>
            <option value="">Not sure, or not on the plan</option>
            {data.data?.items.map((i) => (
              <option key={i.id} value={i.id}>{i.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Started on, optional">
          <input type="date" className={inputClass} value={onsetOn} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setOnsetOn(e.target.value)} />
        </Field>
      </div>
      <Field label="What the patient reported">
        <textarea className={`${inputClass} min-h-[90px]`} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <label className="flex items-center gap-2 text-[14px] text-navy">
        <input type="checkbox" checked={productProblem} onChange={(e) => setProductProblem(e.target.checked)} className="h-4 w-4 accent-[var(--forest)]" />
        Looks like a product problem (damaged, wrong label, warm on arrival)
      </label>
      {save.error && <p className="text-sm text-red-600" role="alert">{save.error instanceof Error ? save.error.message : 'Could not record it'}</p>}
      <div>
        <Button disabled={!severity || description.trim().length < 5} loading={save.isPending} onClick={() => save.mutate()}>
          Record side effect
        </Button>
      </div>
    </div>
  );
}

/** The patient profile card: the reports so far, MedWatch, and the form. */
export function SideEffectsCard({ patientId }: { patientId: string }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const data = useQuery({ queryKey: key(patientId), queryFn: () => api.get<SideEffectsData>(`/patients/${patientId}/adverse-events`) });
  const [adding, setAdding] = useState(false);
  const medwatch = useMutation({
    mutationFn: (eventId: string) => api.post(`/patients/${patientId}/adverse-events/${eventId}/medwatch`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(patientId) }),
  });
  const provider = can('ADMIN', 'CLINICIAN');
  return (
    <Card
      title="Side effects"
      subtitle="What the patient reported, recorded for the legal record. Report serious ones to the FDA through MedWatch."
      action={provider && !adding ? <Button variant="secondary" className="whitespace-nowrap" onClick={() => setAdding(true)}>Record a side effect</Button> : undefined}
    >
      <div data-testid="side-effects">
        {adding && (
          <div className="mb-4 rounded-[12px] border border-hairline bg-[#fbfaf7] p-4">
            <RecordSideEffect patientId={patientId} onDone={() => setAdding(false)} />
          </div>
        )}
        {data.isLoading && <LoadingRows rows={2} />}
        {data.data && data.data.events.length === 0 && !adding && <p className="text-[14px] text-[#5b6b7c]">Nothing recorded.</p>}
        <ul className="divide-y divide-hairline">
          {data.data?.events.map((e) => (
            <li key={e.id} className="py-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex rounded-[5px] px-2 py-[3px] text-[11.5px] font-semibold ${SEVERITY_CHIP[e.severity]}`}>{SEVERITY.find(([v]) => v === e.severity)?.[1]}</span>
                {e.itemName && <span className="text-[13.5px] font-semibold text-navy">{e.itemName}</span>}
                {e.productProblem && <span className="inline-flex rounded-[5px] bg-[#efeee9] px-2 py-[3px] text-[11.5px] text-[#4d6072]">Product problem</span>}
                <span className="ml-auto text-[12.5px] text-[#5b6b7c]">
                  {new Date(e.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  {e.recordedBy ? ` · ${e.recordedBy}` : ''}
                </span>
              </div>
              <p className="mt-1.5 whitespace-pre-line text-[14px] text-navy">{e.description}</p>
              <div className="mt-1.5 flex flex-wrap items-center gap-3 text-[13px]">
                {e.onsetOn && <span className="text-[#5b6b7c]">Started {e.onsetOn}</span>}
                {e.medwatchSubmitted ? (
                  <span className="font-semibold text-forest">Reported to MedWatch{e.medwatchRef ? ` (${e.medwatchRef})` : ''}</span>
                ) : (
                  provider && (
                    <>
                      <a href={data.data?.medwatchUrl} target="_blank" rel="noreferrer" className="font-semibold text-forest underline">Open MedWatch</a>
                      <button type="button" className="font-semibold text-[#4d6072] underline" disabled={medwatch.isPending} onClick={() => medwatch.mutate(e.id)}>
                        Mark as reported
                      </button>
                    </>
                  )
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}
